import React, { useState, useEffect, useRef } from "react";
import * as screeningService from "src/services/screening.service";
import * as notify from "src/services/notification.service";
import { IDigitalWalletScreeningSearchParams } from "src/types";

import DigitalWalletSearchForm from "../components/real-time-screening/DigitalWalletSearchForm";
import DigitalWalletInfo from "src/components/digital-wallet-results/DigitalWalletInfo";
import DigitalWalletSummary from "../components/digital-wallet-results/DigitalWalletSummary";
import EnterDataMessage from "src/components/real-time-screening/EnterDataMessage";
import NoResultsMessage from "src/components/real-time-screening/NoResultsMessage";

const DigitalWalletLightScreeningPage: React.FC = () => {
  const [pristine, setPristine] = useState<boolean>(true);
  const [isScreening, setIsScreening] = useState<boolean>(false);
  const [searchParameters, setSearchParameters] = useState<IDigitalWalletScreeningSearchParams>();
  const [screeningComplete, setScreeningComplete] = useState<boolean>(false);
  const [screenedResults, setScreenedResults] = useState(null);
  let searchParamsChanged = useRef(false);

  const startScreening = () => {
    setIsScreening(true);
    screeningService.screenDigitalWallet(searchParameters!)
      // @ts-ignore
      .then((response) => {
        const data = response.data.data;
        if (!data) {
          notify.error(response.data.error.message);
          setScreenedResults(null);
          setScreeningComplete(false);
          return;
        }
        setScreenedResults(data);
        setScreeningComplete(true);
      })
      .catch(err => {
        if (!err.response.handled) {
          if (err.response && err.response.data && err.response.data.error.message)
            notify.error(err.response.data.error.message);
        }
        setScreenedResults(null);
        setScreeningComplete(false);
      })
      .finally(() => {
        setIsScreening(false);
        if (searchParamsChanged.current) {
          searchParamsChanged.current = false;
        }
      });
  };

  const downloadReportHandler = () => {
    setIsScreening(true);
    screeningService.getDigitalWalletPdf(searchParameters!)
    // @ts-ignore
    .then((response) => {
      const file = new Blob([response.data], {type: 'application/pdf'});
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    })
    .catch(err => {
      if (!err.response.handled) {
        if (err.response && err.response.data && err.response.data.error.message)
          notify.error(err.response.data.error.message);
      }
    })
    .finally(() => {
      setIsScreening(false);
      if (searchParamsChanged.current) {
        searchParamsChanged.current = false;
      }
    });
  };

  const searchParametersChanged = async (params: IDigitalWalletScreeningSearchParams) => {
    setScreeningComplete(false);
    setPristine(false);
    searchParamsChanged.current = true;
    setSearchParameters(params);
  };

  useEffect(() => {
    if (!pristine) {
      startScreening();
    }
  }, [searchParameters, pristine]);

  return (
    <>
      <DigitalWalletSearchForm
        screen={searchParametersChanged}
        screeningComplete={screeningComplete}
      />
      <div className="row">
        { (pristine || (isScreening && !screenedResults)) &&
          <EnterDataMessage
            mt='mt-4'
            message='Enter wallet address to see screening results'
          />
        }
        <div className={`overlay-loader ${isScreening ? 'active' : ''}`}>
          <div className={`loader text-center`}>
            <img src={require('src/assets/images/icons/company_detail_spinner_transparent.gif')} alt="" />
            <p className='color-monoNormal'>Screening...</p>
          </div>
        </div>
        { 
          !pristine
          &&
          screenedResults
          &&
          <div className="col-lg-12 mt-4">
            <div className="d-flex gap-3 align-items-start">
              <div className="wallet-container p-0">
                <DigitalWalletInfo address={searchParameters ? searchParameters.identifier : ''} results={screenedResults} />
              </div>
              <div className="card flex-grow-1 p-0">
                <DigitalWalletSummary 
                  results={screenedResults}
                  downloadReport={downloadReportHandler}
                />
              </div>
            </div>
          </div>
        }
        {
          !pristine
          &&
          !screenedResults
          &&
          !isScreening
          &&
          <NoResultsMessage mt='mt-4' />
        }
      </div>
    </>
  );
};

export default DigitalWalletLightScreeningPage;

